import { Component, ElementRef, ViewChild, AfterViewInit, Output, EventEmitter, Input } from '@angular/core';

@Component({
  selector: 'app-drawing-canvas',
  templateUrl: './drawing-canvas.component.html',
  styleUrls: ['./drawing-canvas.component.scss']
})
export class DrawingCanvasComponent implements AfterViewInit {
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;
  
  @Input() width = 800;
  @Input() height = 500;
  
  @Output() imageChange = new EventEmitter<string>();

  private ctx!: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;

  // Visual settings
  colors = [
    { name: 'dark', hex: '#1E293B' },
    { name: 'blue', hex: '#2563EB' },
    { name: 'red', hex: '#DC2626' },
    { name: 'green', hex: '#16A34A' }
  ];
  
  widths = [
    { label: 'Fine', value: 2 },
    { label: 'Medium', value: 5 },
    { label: 'Thick', value: 9 }
  ];

  selectedColor = '#1E293B';
  selectedWidth = 5;
  isEraser = false;

  // Stack of canvas snapshots for undo/redo
  private history: string[] = [];
  private historyIndex = -1;

  ngAfterViewInit() {
    const canvas = this.canvasRef.nativeElement;
    canvas.width = this.width;
    canvas.height = this.height;
    
    const context = canvas.getContext('2d');
    if (!context) {
      console.error('Could not acquire 2D canvas context');
      return;
    }
    
    this.ctx = context;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    
    // Fill canvas with white background so transparency is not saved
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.fillRect(0, 0, this.width, this.height);
    
    // Save initial blank state to history
    this.saveState();
  }

  // Pointer event listeners (Stylus/Finger/Mouse compatible)
  startDrawing(event: PointerEvent) {
    event.preventDefault();
    this.isDrawing = true;
    
    const { x, y } = this.getCanvasCoordinates(event);
    this.lastX = x;
    this.lastY = y;
    
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
  }

  draw(event: PointerEvent) {
    if (!this.isDrawing) return;
    event.preventDefault();
    
    const { x, y } = this.getCanvasCoordinates(event);
    
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.lineTo(x, y);
    
    this.ctx.strokeStyle = this.isEraser ? '#FFFFFF' : this.selectedColor;
    this.ctx.lineWidth = this.selectedWidth;
    this.ctx.stroke();
    
    this.lastX = x;
    this.lastY = y;
  }

  stopDrawing(event: PointerEvent) {
    if (!this.isDrawing) return;
    event.preventDefault();
    this.isDrawing = false;
    
    this.saveState();
    this.emitImage();
  }

  private getCanvasCoordinates(event: PointerEvent): { x: number; y: number } {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (event.clientX - rect.left) * scaleX,
      y: (event.clientY - rect.top) * scaleY
    };
  }

  selectColor(hex: string) {
    this.selectedColor = hex;
    this.isEraser = false;
  }

  selectWidth(width: number) {
    this.selectedWidth = width;
  }

  toggleEraser(active: boolean) {
    this.isEraser = active;
  }

  clearCanvas() {
    this.ctx.fillStyle = '#FFFFFF';
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.isDrawing = false;
    
    this.saveState();
    this.emitImage();
  }

  // History Undo & Redo logic
  undo() {
    if (this.historyIndex > 0) {
      this.historyIndex--;
      this.restoreState();
    }
  }

  redo() {
    if (this.historyIndex < this.history.length - 1) {
      this.historyIndex++;
      this.restoreState();
    }
  }

  get canUndo(): boolean {
    return this.historyIndex > 0;
  }

  get canRedo(): boolean {
    return this.historyIndex < this.history.length - 1;
  }

  private saveState() {
    const canvas = this.canvasRef.nativeElement;
    const dataUrl = canvas.toDataURL('image/png');
    
    // Clear redo history if we are overwriting it
    if (this.historyIndex < this.history.length - 1) {
      this.history.splice(this.historyIndex + 1);
    }
    
    this.history.push(dataUrl);
    this.historyIndex = this.history.length - 1;
  }

  private restoreState() {
    const dataUrl = this.history[this.historyIndex];
    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      this.ctx.fillStyle = '#FFFFFF';
      this.ctx.fillRect(0, 0, this.width, this.height);
      this.ctx.drawImage(img, 0, 0);
      this.emitImage();
    };
  }

  private emitImage() {
    const canvas = this.canvasRef.nativeElement;
    this.imageChange.emit(canvas.toDataURL('image/png'));
  }
}
